import { autoUpdate, flip, FloatingPortal, offset, shift, useFloating } from '@floating-ui/react';
import React from 'react';
import { AirportTaf, AirportTafItem } from '../../../airports/airport';
import { AirportFlightCategory } from '../../../common/enums';
import './TafBar.css';
import { useMultiWindowContext } from '../../components/multi-window/context/multi-window-context';

const SCALE = 108_000; // 30 hours in seconds
const HOURS = 30;

interface CategoryStyle {
    bg: string;
    fg: string;
    label: string;
}

const CATEGORY_STYLE: Record<number, CategoryStyle> = {
    [AirportFlightCategory.unknown]: { bg: '#ffffff', fg: '#333', label: 'N/A' },
    [AirportFlightCategory.VFR]: { bg: '#00ff00', fg: '#000', label: 'VFR' },
    [AirportFlightCategory.MVFR]: { bg: '#ffff00', fg: '#000', label: 'MVFR' },
    [AirportFlightCategory.IFR]: { bg: '#ff9900', fg: '#000', label: 'IFR' },
    [AirportFlightCategory.LIFR]: { bg: '#ff00ff', fg: '#fff', label: 'LIFR' },
    [AirportFlightCategory.BCAT1]: { bg: '#ff0000', fg: '#fff', label: 'BCAT1' }
};

function tafColor(category: AirportFlightCategory): string {
    return CATEGORY_STYLE[category]?.bg ?? '#ffffff';
}

function degreesToCardinal(deg: number): string {
    const dirs = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW', 'N'] as const;
    return dirs[Math.round(deg / 45) % 8];
}

function formatWind(item: AirportTafItem): string | null {
    if (item.windSpeed == null) {
        return null;
    }
    const dirNum = typeof item.windDirection === 'number' ? item.windDirection : null;
    const dirStr =
        item.windDirection == null
            ? 'VRB'
            : item.windDirection === 'variable'
              ? 'VRB'
              : `${String(dirNum).padStart(3, '0')}° ${degreesToCardinal(dirNum!)}`;
    const gust = item.windGust != null ? ` G${item.windGust}` : '';
    return `${dirStr} ${item.windSpeed}${gust} KT`;
}

function formatVisibility(item: AirportTafItem): string | null {
    if (item.visibility == null) {
        return null;
    }
    if (item.visibility === 'CAVOK') {
        return 'CAVOK';
    }
    if (item.visibility === '>10.5miles') {
        return '>10.5 SM';
    }
    return `${item.visibility} SM`;
}

function formatCeiling(item: AirportTafItem): string | null {
    if (item.ceiling == null) {
        return null;
    }
    if (item.ceiling === '>12,000') {
        return '>12,000 ft';
    }
    return `${(item.ceiling as number).toLocaleString('en-US')} ft`;
}

function getTafTypeLabel(item: AirportTafItem): string {
    if (item.type === 80) {
        return 'TEMPO';
    }
    if (item.type === 84) {
        return item.probability != null ? `PROB${item.probability}` : 'PROB';
    }
    return 'FM';
}

function formatHourUtc(seconds: number): string {
    const d = new Date(seconds * 1000);
    const hh = String(d.getUTCHours()).padStart(2, '0');
    const mm = String(d.getUTCMinutes()).padStart(2, '0');
    return `${hh}:${mm} UTC`;
}

interface ActiveForecasts {
    base: AirportTafItem | null;
    worstOverlay: AirportTafItem | null;
}

function getForecastsAtHour(tafs: AirportTaf, hourIndex: number): ActiveForecasts {
    if (tafs.items.length === 0) {
        return { base: null, worstOverlay: null };
    }
    const firstTime = tafs.items[0].startTime!;
    const hourStart = firstTime + hourIndex * 3600;
    const hourEnd = hourStart + 3600;

    const baseItems = tafs.items.filter(
        (x) =>
            x.type !== 80 &&
            x.type !== 84 &&
            x.startTime != null &&
            x.startTime <= hourStart &&
            (x.endTime == null || x.endTime > hourStart)
    );
    const base = baseItems.length > 0 ? baseItems[baseItems.length - 1] : null;

    const overlays = tafs.items.filter(
        (x) =>
            (x.type === 80 || x.type === 84) &&
            x.startTime != null &&
            x.startTime < hourEnd &&
            (x.endTime == null || x.endTime > hourStart)
    );

    const worstOverlay =
        overlays.length === 0
            ? null
            : overlays.reduce((worst, x) => (x.flightCategory > worst.flightCategory ? x : worst), overlays[0]);

    return { base, worstOverlay };
}

export function FlightCategoryBadge({ category }: { category: AirportFlightCategory }) {
    const style = CATEGORY_STYLE[category] ?? CATEGORY_STYLE[AirportFlightCategory.unknown];
    return (
        <span className="taf-category-badge" style={{ backgroundColor: style.bg, color: style.fg }}>
            {style.label}
        </span>
    );
}

function ForecastSection({ item }: { item: AirportTafItem }) {
    const typeLabel = getTafTypeLabel(item);
    const wind = formatWind(item);
    const vis = formatVisibility(item);
    const ceil = formatCeiling(item);

    const rows: { label: string; value: string }[] = [];
    if (wind) {
        rows.push({ label: 'Wind', value: wind });
    }
    if (vis) {
        rows.push({ label: 'Vis', value: vis });
    }
    if (ceil) {
        rows.push({ label: 'Ceiling', value: ceil });
    }

    return (
        <div className="tooltipSection">
            <div className="taf-tooltip-section-header">
                <FlightCategoryBadge category={item.flightCategory} />
                <span className="taf-tooltip-type-label">{typeLabel}</span>
            </div>
            {rows.length > 0 && (
                <div className="taf-tooltip-grid">
                    {rows.map((r) => (
                        <React.Fragment key={r.label}>
                            <span className="taf-tooltip-grid-label">{r.label}</span>
                            <span className="taf-tooltip-grid-value">{r.value}</span>
                        </React.Fragment>
                    ))}
                </div>
            )}
        </div>
    );
}

function TafTooltipContent({ tafs, hourIndex }: { tafs: AirportTaf; hourIndex: number }) {
    const firstTime = tafs.items[0]?.startTime;
    const timeLabel = firstTime != null ? formatHourUtc(firstTime + hourIndex * 3600) : '';
    const { base, worstOverlay } = getForecastsAtHour(tafs, hourIndex);

    const allSections = [...(base ? [base] : []), ...(worstOverlay ? [worstOverlay] : [])];

    if (allSections.length === 0) {
        return null;
    }

    return (
        <div className="taf-tooltip">
            <div className="tooltipTime">{timeLabel}</div>
            {allSections.map((item, i) => (
                <ForecastSection key={i} item={item} />
            ))}
        </div>
    );
}

interface TafBarProps {
    tafs: AirportTaf;
}

export function TafBar({ tafs }: TafBarProps) {
    const [hoveredHour, setHoveredHour] = React.useState<number | null>(null);
    const multiWindowContext = useMultiWindowContext();

    const { refs, floatingStyles, isPositioned } = useFloating({
        open: hoveredHour !== null,
        placement: 'top',
        strategy: 'fixed',
        middleware: [offset(8), flip(), shift({ padding: 4 })],
        whileElementsMounted: autoUpdate
    });

    const handleSegmentEnter = React.useCallback(
        (e: React.MouseEvent<HTMLDivElement>, hour: number) => {
            refs.setReference(e.currentTarget);
            setHoveredHour(hour);
        },
        [refs]
    );

    const handleSegmentLeave = React.useCallback(() => {
        setHoveredHour(null);
    }, []);

    if (tafs.items.length === 0) {
        return <div style={{ height: 30, position: 'relative' }} />;
    }

    const firstTime = tafs.items[0].startTime!;

    return (
        <div style={{ height: 30, position: 'relative' }}>
            <div className="taf-bars-border" style={{ inset: '2px', position: 'absolute', overflow: 'hidden' }}>
                {/* Base forecast bars */}
                {tafs.items
                    .filter((x) => x.type !== 80 && x.type !== 84 && x.startTime != null && x.endTime != null)
                    .map((x, i) => {
                        const left = -firstTime + x.startTime!;
                        const right = -firstTime + x.endTime!;
                        return (
                            <div
                                key={i}
                                style={{
                                    position: 'absolute',
                                    top: '0',
                                    bottom: '0',
                                    left: `${((left / SCALE) * 100).toFixed(0)}%`,
                                    right: `${(100 - (right / SCALE) * 100).toFixed(0)}%`,
                                    backgroundColor: tafColor(x.flightCategory)
                                }}
                            />
                        );
                    })}

                {/* TEMPO / PROB bars */}
                {tafs.items
                    .filter((x) => (x.type === 80 || x.type === 84) && x.startTime != null && x.endTime != null)
                    .map((x, i) => {
                        const left = -firstTime + x.startTime!;
                        const right = -firstTime + x.endTime!;
                        const style: React.CSSProperties = {
                            position: 'absolute',
                            height: '20%',
                            border: '1px solid black',
                            left: `${((left / SCALE) * 100).toFixed(0)}%`,
                            right: `${(100 - (right / SCALE) * 100).toFixed(0)}%`,
                            backgroundColor: tafColor(x.flightCategory)
                        };
                        if (x.type === 80) {
                            style.top = '10%';
                        } else {
                            style.bottom = '10%';
                        }
                        return <div key={i} style={style} />;
                    })}

                {/*We do gradient instead of div border to fix subpixel rendering issues on lower res screens*/}
                <div className="taf-ticks-overlay" style={{ backgroundSize: `calc(100% / ${HOURS}) 100%` }} />

                {/* Invisible hover segments */}
                {Array.from({ length: HOURS }, (_, i) => (
                    <div
                        key={`seg-${i}`}
                        className="taf-hour-segment"
                        style={{
                            left: `${((i / HOURS) * 100).toFixed(3)}%`,
                            width: `${(100 / HOURS).toFixed(3)}%`
                        }}
                        onMouseEnter={(e) => handleSegmentEnter(e, i)}
                        onMouseLeave={handleSegmentLeave}
                    />
                ))}
            </div>

            {hoveredHour !== null && (
                <FloatingPortal root={multiWindowContext.root}>
                    <div
                        ref={refs.setFloating}
                        style={{
                            ...floatingStyles,
                            visibility: isPositioned ? 'visible' : 'hidden',
                            zIndex: 1000
                        }}>
                        <TafTooltipContent tafs={tafs} hourIndex={hoveredHour} />
                    </div>
                </FloatingPortal>
            )}
        </div>
    );
}
